import type { IHasClassName, IUserTransaction } from '@/interfaces';
import { DataTablePagination, Skeleton } from '@/components/custom';
import DataTableHeader from './DataTableHeader';
import DataTableBody from './DataTableBody';
import DataTableMobileCard from './DataTableMobileCard';
import DataTableMobileSkeleton from './DataTableMobileSkeleton';
import { Table, TableBody, TableRow, TableCell } from '@/components/ui';
import { cn } from '@/lib/utils';

type TProps = IHasClassName & {
	transactions: IUserTransaction[];
	pageCount: number;
	isLoading?: boolean;
};

const DataTableBodySkeleton = ({ rows = 8 }: { rows?: number }) => {
	const skeletonKeys = Array.from({ length: rows }, () => crypto.randomUUID());
	return (
		<TableBody>
			{skeletonKeys.map(key => (
				<TableRow key={key} className="h-12">
					<TableCell className="w-[110px] py-1">
						<Skeleton className="h-4 w-20" />
					</TableCell>
					<TableCell className="w-[100px] py-1">
						<Skeleton className="h-4 w-16" />
					</TableCell>
					<TableCell className="w-[140px] py-1">
						<Skeleton className="h-4 w-24" />
					</TableCell>
					<TableCell className="w-[120px] py-1 text-left">
						<Skeleton className="h-4 w-16" />
					</TableCell>
					<TableCell className="w-0 py-1">
						<Skeleton className="h-4 w-32" />
					</TableCell>
					<TableCell className="w-0 py-1">
						<Skeleton className="h-4 w-20" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);
};

export const DataTable = ({ transactions, pageCount, className, isLoading }: TProps) => (
	<div className={cn('space-y-5', className)}>
		<div className="hidden md:block border rounded-md">
			<Table>
				<DataTableHeader />
				{isLoading ? <DataTableBodySkeleton rows={8} /> : <DataTableBody transactions={transactions} />}
			</Table>
		</div>
		<div className="block md:hidden">
			{isLoading ? (
				<DataTableMobileSkeleton count={5} />
			) : (
				<div className="space-y-4">
					{transactions.map(transaction => (
						<DataTableMobileCard key={transaction._id} transaction={transaction} />
					))}
				</div>
			)}
		</div>
		{!isLoading && <DataTablePagination pageCount={pageCount} />}
	</div>
);

export default DataTable;
